import { useState, useEffect } from 'react';
import { Dayjs } from 'dayjs';
import { BulkOrderItem } from '../../../../app/services/api/orderManagementClient';
import { PredictedDemandModel, podClient, ItemConversion } from '../../../../app/services/api/podClient';
import { isPodEnabledEnvironment } from '../../../../app/services/environment';

interface SelectedStore {
  storeNumber?: string;
  franchiseName?: string;
}

interface UsePredictedDemandsOptions {
  selectedStore: SelectedStore | null | undefined;
  bulkOrderItems: BulkOrderItem[];
  selectedDate: Dayjs | null;
  bulkOrdersLoadVersion: number;
  logError: (error: unknown) => void;
}

export const usePredictedDemands = ({
  selectedStore,
  bulkOrderItems,
  selectedDate,
  bulkOrdersLoadVersion,
  logError,
}: UsePredictedDemandsOptions) => {
  const [isPodEnabled, setIsPodEnabled] = useState<boolean>(false);
  const [predictedDemands, setPredictedDemands] = useState<Record<string, PredictedDemandModel[]>>({});

  useEffect(() => {
    if (isPodEnabledEnvironment()) {
      loadPodStoreConfiguration();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStore?.storeNumber]);

  useEffect(() => {
    if (isPodEnabled && bulkOrderItems.length > 0 && selectedDate?.isValid() && selectedStore?.franchiseName) {
      loadPredictedDemands();
    } else {
      setPredictedDemands({});
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPodEnabled, bulkOrdersLoadVersion, selectedDate]);

  const loadPodStoreConfiguration = async () => {
    try {
      if (selectedStore?.franchiseName && selectedStore.storeNumber) {
        const response = await podClient.getStoreConfiguration(selectedStore.franchiseName, selectedStore.storeNumber);
        setIsPodEnabled(response.isPodEnabled);
      }
    } catch (error) {
      logError(error);
    }
  };

  const loadPredictedDemands = async () => {
    if (!isPodEnabled) return;
    if (!selectedDate?.isValid() || !selectedStore?.franchiseName) return;

    const franchiseName: string = selectedStore.franchiseName;
    const podDeliveryDate = selectedDate.format('DD-MM-YYYY');
    const uniqueStores = Array.from(new Set(bulkOrderItems.map(i => i.storeNumber)));
    const allItemSkus = Array.from(new Set(bulkOrderItems.map(i => i.itemSku)));

    const packSizeBySkuMap = new Map<string, string>();
    bulkOrderItems.forEach(i => {
      if (!packSizeBySkuMap.has(i.itemSku) && i.packSize) {
        packSizeBySkuMap.set(i.itemSku, i.packSize);
      }
    });

    const conversions: ItemConversion[] = allItemSkus.map(sku => {
      const rawPackSize = packSizeBySkuMap.get(sku);
      const conversionNumber = rawPackSize != null ? (parseFloat(rawPackSize) || 1) : 1;
      return { itemNumber: sku, conversionNumber, isFrozen: false };
    });

    // Process stores in batches of 5 to avoid overwhelming the POD service
    const CHUNK_SIZE = 5;
    const chunks: string[][] = [];
    for (let i = 0; i < uniqueStores.length; i += CHUNK_SIZE) {
      chunks.push(uniqueStores.slice(i, i + CHUNK_SIZE));
    }

    const allResults: PromiseSettledResult<{ storeNumber: string; predictions: PredictedDemandModel[] }>[] = [];
    for (const chunk of chunks) {
      const chunkResults = await Promise.allSettled(
        chunk.map(async (storeNumber) => {
          const predictions = await podClient.getPredictionsForItems(
            franchiseName,
            storeNumber,
            [podDeliveryDate],
            allItemSkus,
            conversions,
          );
          return { storeNumber, predictions };
        }),
      );
      allResults.push(...chunkResults);
    }

    const demandsByStore: Record<string, PredictedDemandModel[]> = {};
    allResults.forEach(result => {
      if (result.status === 'fulfilled') {
        demandsByStore[result.value.storeNumber] = result.value.predictions;
      }
    });

    setPredictedDemands(demandsByStore);
  };

  return { isPodEnabled, predictedDemands };
};
